import { TFunction } from "i18next";
import { ColumnDef, SortingFn } from "@tanstack/react-table";
import { Chip, Link, Stack } from "@mui/material";
import LockOutlineIcon from "@mui/icons-material/LockOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ExamDateDto, ExamFormat } from "api";
import { formatDate } from "utils";

export const getColumns = (
  t: TFunction<"translation", undefined>,
  onSelectExam: (exam: ExamDateDto) => void
): ColumnDef<ExamDateDto>[] => [
  {
    accessorKey: "examName",
    header: () => t("selectExam.table.exam"),
    size: 20,
    cell: (info) => info.getValue(),
    sortingFn: "alphanumeric",
  },
  {
    accessorKey: "examDate",
    header: () => t("selectExam.table.date"),
    size: 10,
    cell: ({ row }) => formatDate(row.original.examDate),
  },
  {
    accessorKey: "registrationEndDate",
    header: () => t("selectExam.table.deadlineForRegistration"),
    size: 10,
    cell: ({ row }) => formatDate(row.original.registrationEndDate),
  },
  {
    accessorKey: "speakingDates",
    header: () => t("selectExam.table.speakingTest"),
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "city",
    header: () => t("selectExam.table.city"),
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "examFormat",
    header: () => t("selectExam.table.format"),
    size: 10,
    cell: ({ row }) =>
      row.original.examFormat === ExamFormat.Pb
        ? t("common.paperBased")
        : t("common.computerBased"),
  },
  {
    accessorKey: "price",
    header: () => t("selectExam.table.price"),
    size: 10,
    cell: ({ row }) => `${row.original.price} ${row.original.currency}`,
  },
  {
    accessorKey: "slotsLeft",
    header: () => t("selectExam.table.slotsLeft"),
    size: 10,
    cell: ({ row }) =>
      row.original.slotsLeft ?? <CheckCircleIcon color="success" />,
    sortingFn: numberWithNullsSort,
  },
  {
    accessorKey: "registrationClosed",
    header: "",
    size: 10,
    enableSorting: false,
    cell: ({ row }) => (
      <Stack sx={{ alignItems: "center" }}>
        {registrationClosed(row.original) ? (
          <Chip
            label={t("selectExam.table.registrationClosed")}
            color="error"
            variant="colored"
            icon={<LockOutlineIcon />}
            size="large"
          />
        ) : (
          <Link
            onClick={() => onSelectExam(row.original)}
            underline="hover"
            variant="body1"
            sx={{ cursor: "pointer" }}
          >
            {t("selectExam.table.registerLink")} <ChevronRightIcon />
          </Link>
        )}
      </Stack>
    ),
  },
];

export const numberWithNullsSort: SortingFn<ExamDateDto> = (
  rowA,
  rowB,
  columnId
) => {
  const a = rowA.getValue<number | null>(columnId);
  const b = rowB.getValue<number | null>(columnId);

  if (a === null && b === null) return 0;
  if (a === null) return -1;
  if (b === null) return 1;

  return a - b;
};

export const registrationClosed = (row: ExamDateDto) =>
  row.registrationClosed || row.slotsLeft === 0;
